import type * as React from "react";
import { Navigate, useLocation } from "react-router-dom";

import {
	UserProfileProvider,
	useUserProfileContext,
} from "@/contexts/UserProfileContext";
import { isAllowedInternalEmail } from "@/lib/internal-email-domain";
import {
	canAccessInternalPath,
	getDefaultInternalPathForRole,
	isInternalUser,
} from "@/lib/roles";
import {
	buildRequiredTwoFactorSetupUrl,
	hasTwoFactorEnabled,
} from "@/lib/two-factor-policy";

interface InternalRouteGuardProps {
	children: React.ReactNode;
	requiredPath: string;
}

export function InternalRouteGuard({
	children,
	requiredPath,
}: InternalRouteGuardProps) {
	return (
		<UserProfileProvider>
			<InternalRouteGuardInner requiredPath={requiredPath}>
				{children}
			</InternalRouteGuardInner>
		</UserProfileProvider>
	);
}

function InternalRouteGuardInner({
	children,
	requiredPath,
}: InternalRouteGuardProps) {
	const {
		user,
		profile,
		userRole,
		isPending,
		isProfileLoading,
		isAuthenticated,
		isAuthBridgeReady,
	} = useUserProfileContext();
	const location = useLocation();

	if (!isAuthBridgeReady || isPending || isProfileLoading) {
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="text-muted-foreground">Verifying access...</div>
			</div>
		);
	}

	if (!isAuthenticated) {
		const target = `${location.pathname}${location.search}`;
		return (
			<Navigate to={`/login?redirect=${encodeURIComponent(target)}`} replace />
		);
	}

	const userEmail =
		(user as { email?: string } | undefined)?.email ??
		(profile as { email?: string } | undefined)?.email;

	if (!isAllowedInternalEmail(userEmail)) {
		return <Navigate to="/login?error=invalid-domain" replace />;
	}

	if (userRole !== undefined && !isInternalUser(userRole)) {
		return <Navigate to="/login?error=unauthorized" replace />;
	}

	if (
		!hasTwoFactorEnabled(
			user as { twoFactorEnabled?: boolean | null } | undefined,
		) &&
		location.pathname !== "/security"
	) {
		const target = `${location.pathname}${location.search}`;
		return <Navigate to={buildRequiredTwoFactorSetupUrl(target)} replace />;
	}

	if (
		userRole !== undefined &&
		!canAccessInternalPath(userRole, requiredPath, userEmail)
	) {
		return <Navigate to={getDefaultInternalPathForRole(userRole)} replace />;
	}

	return <>{children}</>;
}
